import type { LeagueRow, RaceResult, RecentRace, TeamResult } from '@typing-racer/shared';
import { clear, h, ordinal, pct, VEHICLE_EMOJI } from '../dom.js';

export function renderResults(
  root: HTMLElement,
  results: RaceResult[],
  myId: string | null,
  online: boolean,
  actions: { again(): void; league(): void; leave(): void; progress?(): void },
  coaching: { title: string; body: string }[] | null = null,
  teams: TeamResult[] | null = null,
  quiz: { score: number; total: number } | null = null,
): void {
  clear(root);
  const me = results.find((r) => r.id === myId);
  const myTeam = teams && me ? teams.find((t) => t.members.includes(me.name)) : null;
  const medal = (p: number) => ['🥇', '🥈', '🥉'][p - 1] ?? ordinal(p);
  root.append(
    h(
      'div',
      { class: 'center' },
      h(
        'div',
        { class: 'panel results stack' },
        h('h1', {}, me ? `${medal(me.place)} You finished ${ordinal(me.place)}` : 'Race over'),
        teams && teams.length
          ? h(
              'div',
              { class: 'teams-result' },
              myTeam ? h('h2', { style: `color:${myTeam.colour}` }, myTeam.place === 1 ? `🏆 Team ${myTeam.name} wins!` : `Team ${myTeam.name} finished ${ordinal(myTeam.place)}`) : h('h2', {}, 'Team standings'),
              h(
                'table',
                {},
                h('thead', {}, h('tr', {}, h('th', {}, '#'), h('th', {}, 'Team'), h('th', {}, 'Members'), h('th', { class: 'num' }, 'Score'))),
                h('tbody', {}, teams.map((t) => h('tr', { class: myTeam && t.team === myTeam.team ? 'me' : '' }, h('td', { class: 'place' }, medal(t.place)), h('td', {}, h('span', { class: 'badge team', style: `background:${t.colour};color:#fff` }, t.name)), h('td', {}, t.members.join(', ')), h('td', { class: 'num' }, h('b', {}, String(t.score)))))),
              ),
            )
          : null,
        online ? null : h('p', { class: 'muted small', style: 'margin:0' }, 'Practice race: no league points, but it counts towards your personal stats when signed in.'),
        quiz ? h('p', { class: 'quiz-score' }, `🧠 Recall quiz: you remembered ${quiz.score} of ${quiz.total}${quiz.score === quiz.total ? '. Perfect!' : quiz.score === 0 ? '. Try reading the sentences, not just the letters.' : '.'}`) : null,
        h(
          'table',
          {},
          h('thead', {}, h('tr', {}, h('th', {}, '#'), h('th', {}, 'Player'), h('th', { class: 'num' }, 'WPM'), h('th', { class: 'num' }, 'Accuracy'), h('th', { class: 'num' }, 'Time'), h('th', { class: 'num' }, 'Points'))),
          h(
            'tbody',
            {},
            results.map((r) =>
              h(
                'tr',
                { class: r.id === myId ? 'me' : '' },
                h('td', { class: 'place' }, medal(r.place)),
                h('td', {}, h('span', { class: 'dot', style: `display:inline-block;width:12px;height:12px;border-radius:4px;border:2px solid #1b2430;background:${r.colour};margin-right:6px;vertical-align:middle` }), `${VEHICLE_EMOJI[r.vehicle] ?? ''} ${r.name}`, r.isBot ? h('span', { class: 'badge bot', style: 'margin-left:6px' }, 'bot') : null, r.bestAccuracy ? h('span', { class: 'badge ready', style: 'margin-left:6px' }, '+2 accuracy') : null),
                h('td', { class: 'num' }, String(Math.round(r.summary.wpm))),
                h('td', { class: 'num' }, pct(r.summary.accuracy)),
                h('td', { class: 'num' }, `${(r.summary.durationMs / 1000).toFixed(1)}s`),
                h('td', { class: 'num' }, r.isBot || !online ? '-' : String(r.points)),
              ),
            ),
          ),
        ),
        me && me.hints.length
          ? h('div', {}, h('h2', {}, '💡 Your typing hints'), me.hints.map((hint) => h('div', { class: 'hint' }, h('b', {}, hint.title), hint.body)))
          : null,
        coaching
          ? h('div', {}, h('h2', {}, '🧭 Your progress'), coaching.map((c) => h('div', { class: 'hint' }, h('b', {}, c.title), c.body)), actions.progress ? h('div', { style: 'margin-top:8px' }, h('button', { class: 'btn small blue', onclick: actions.progress }, '📈 Full progress report')) : null)
          : null,
        h(
          'div',
          { class: 'row' },
          h('button', { class: 'btn primary', onclick: actions.again }, online ? '🔁 Back to lobby' : '🔁 Race again'),
          h('button', { class: 'btn ghost', onclick: actions.league }, '🏆 League'),
          h('button', { class: 'btn small red', onclick: actions.leave }, 'Leave'),
        ),
      ),
    ),
  );
}

export function renderLeague(
  root: HTMLElement,
  rows: LeagueRow[],
  recent: RecentRace[],
  myName: string,
  onBack: () => void,
): void {
  clear(root);
  const fmtDate = (iso: string) => new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
  root.append(
    h(
      'div',
      { class: 'center' },
      h(
        'div',
        { class: 'panel wide stack' },
        h('h1', {}, '🏆 League table'),
        rows.length === 0
          ? h('p', { class: 'muted' }, 'No races yet. Be the first on the board!')
          : h(
              'table',
              {},
              h('thead', {}, h('tr', {}, h('th', {}, '#'), h('th', {}, 'Player'), h('th', { class: 'num' }, 'Points'), h('th', { class: 'num' }, 'Races'), h('th', { class: 'num' }, 'Wins'), h('th', { class: 'num' }, 'Podiums'), h('th', { class: 'num' }, 'Best WPM'), h('th', { class: 'num' }, 'Avg WPM'), h('th', { class: 'num' }, 'Avg acc.'))),
              h(
                'tbody',
                {},
                rows.map((r, i) =>
                  h(
                    'tr',
                    { class: r.name.toLowerCase() === myName.toLowerCase() ? 'me' : '' },
                    h('td', { class: 'place' }, String(i + 1)),
                    h('td', {}, r.name),
                    h('td', { class: 'num' }, h('b', {}, String(r.points))),
                    h('td', { class: 'num' }, String(r.races)),
                    h('td', { class: 'num' }, String(r.wins)),
                    h('td', { class: 'num' }, String(r.podiums)),
                    h('td', { class: 'num' }, String(r.bestWpm)),
                    h('td', { class: 'num' }, String(r.avgWpm)),
                    h('td', { class: 'num' }, pct(r.avgAccuracy)),
                  ),
                ),
              ),
            ),
        recent.length
          ? h(
              'div',
              {},
              h('h2', {}, 'Recent races'),
              h('table', {}, h('tbody', {}, recent.map((r) => h('tr', {}, h('td', {}, fmtDate(r.playedAt)), h('td', {}, `Winner: ${r.winner}`), h('td', { class: 'num' }, `${r.players} player${r.players === 1 ? '' : 's'}`), h('td', { class: 'num' }, `best ${r.bestWpm} WPM`))))),
            )
          : null,
        h('div', {}, h('button', { class: 'btn', onclick: onBack }, '← Back')),
      ),
    ),
  );
}
